import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartContainer } from './ChartContainer';
import { ChartTooltipContent } from './ChartTooltip';
import type { ChartConfig } from './chart-utils';
import { CHART_COLORS } from './chart-utils';

interface PieChartProps {
  data: Array<{ name: string; value: number; [key: string]: unknown }>;
  config: ChartConfig;
  height?: number;
  unit?: string;
  className?: string;
  donut?: boolean;
  /** Genestete Daten: aeusserer Ring */
  outerData?: Array<{ name: string; value: number; [key: string]: unknown }>;
  outerConfig?: ChartConfig;
  /** Legendenfarbe (CSS-Wert). Fuer Charts auf dunklem Hintergrund z.B. "white" oder "var(--color-brand-surface)" */
  legendColor?: string;
  /** Screenreader-Label fuer den Chart */
  ariaLabel?: string;
  /** Erzwungene Nachkommastellen im Tooltip. `undefined` = auto (0–2). */
  decimals?: number;
}

/**
 * PieChart - Kreisdiagramm, optional als Donut oder mit genesteten Ringen.
 *
 * @example Einfach:
 * data={[{ name: 'strom', value: 45 }, { name: 'gas', value: 30 }]}
 * config={{ strom: { label: 'Strom', color: '...' }, gas: { label: 'Gas', color: '...' } }}
 *
 * @example Donut:
 * donut={true}
 *
 * @example Genestete Ringe:
 * data={[{ name: 'privat', value: 60 }, { name: 'gewerbe', value: 40 }]}
 * outerData={[{ name: 'strom_p', value: 35 }, { name: 'gas_p', value: 25 }, ...]}
 */
export function CPPieChart({
  data,
  config,
  legendColor,
  height = 350,
  unit,
  className,
  donut = false,
  outerData,
  outerConfig,
  ariaLabel,
  decimals,
}: PieChartProps) {
  const mergedConfig = { ...config, ...(outerConfig ?? {}) };

  return (
    <ChartContainer config={mergedConfig} height={height} className={className} ariaLabel={ariaLabel}>
      <RechartsPieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
        <Tooltip content={<ChartTooltipContent config={mergedConfig} unit={unit} decimals={decimals} />} />
        {/* itemSorter=null: Recharts sortiert die Legende per Default alphabetisch
            nach name ('value'); null behält die Daten-/Editor-Reihenfolge. */}
        <Legend
          itemSorter={null}
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: '13px', ...(legendColor ? { color: legendColor } : {}) }}
          formatter={(value: string) => (
            <span style={legendColor ? { color: legendColor } : undefined}>
              {mergedConfig[value]?.label ?? value}
            </span>
          )}
        />
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={donut || outerData ? '40%' : 0}
          outerRadius={outerData ? '60%' : '80%'}
          paddingAngle={0}
          strokeWidth={0}
        >
          {data.map((entry) => (
            <Cell
              key={entry.name}
              fill={config[entry.name]?.color ?? CHART_COLORS[data.indexOf(entry) % CHART_COLORS.length]}
            />
          ))}
        </Pie>
        {outerData && outerConfig && (
          <Pie
            data={outerData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="65%"
            outerRadius="80%"
            paddingAngle={2}
            strokeWidth={0}
          >
            {outerData.map((entry) => (
              <Cell
                key={entry.name}
                fill={outerConfig[entry.name]?.color ?? CHART_COLORS[outerData.indexOf(entry) % CHART_COLORS.length]}
              />
            ))}
          </Pie>
        )}
      </RechartsPieChart>
    </ChartContainer>
  );
}
